import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SideMenuNode, SideMenuSelectEvent } from '../../../../shared/components/sidemenu/sidemenu.model';
import { FilterRow, hasDuplicateFilterNames, isFilterRowValid } from '../add-filter/add-filter.component';

/**
 * Body of the "Add filter" popup: ASK / Visual Editor / JSON Editor / SQL Query tabs via app-sidemenu.
 * Hosted by InbuiltCustomReportComponent inside its own <app-modal>; the modal footer drives this
 * component's public state/methods (primaryActionLabel/canPerformPrimaryAction/onPrimaryAction), the
 * same pattern used by select-datasource-modal's #dsBody.
 */
@Component({
  selector: 'app-add-filter-modal',
  standalone: false,
  templateUrl: './add-filter-modal.component.html',
  styleUrl: './add-filter-modal.component.scss'
})
export class AddFilterModalComponent {
  @Output() cancelled = new EventEmitter<void>();
  @Output() filterCreated = new EventEmitter<any>();
  // Report's datasource config, forwarded straight down to the Visual Editor's data column picker.
  @Input() selectedDataSource: any = null;

  readonly menuItems: SideMenuNode[] = [
    { id: 'ask', type: 'ask', name: 'ASK' },
    { id: 'visual-editor', type: 'editor', name: 'Visual Editor' },
    { id: 'json-editor', type: 'editor', name: 'JSON Editor' },
    { id: 'sql-query', type: 'query', name: 'SQL Query' }
  ];
  selectedMenuId = 'ask';

  askPromptValue = '';
  // Visual Editor / JSON Editor / SQL Query all start empty until the user fills them in (or ASK generates a draft).
  filterRows: FilterRow[] = [];
  jsonEditorInitialJson: any = {};
  sqlQueryCode = '';

  onMenuSelected(event: SideMenuSelectEvent): void {
    if (event?.node?.id) {
      this.selectedMenuId = event.node.id;
    }
  }

  // app-modal keeps this component mounted between opens (see select-datasource-modal-pattern memory) -
  // the host must call this before showing the modal again so a previous draft isn't still here.
  resetState(): void {
    this.selectedMenuId = 'ask';
    this.askPromptValue = '';
    this.filterRows = [];
    this.jsonEditorInitialJson = {};
    this.sqlQueryCode = '';
  }

  onFilterDraftChange(rows: FilterRow[]): void {
    this.filterRows = rows;
  }

  onJsonTextChange(value: any): void {
    this.jsonEditorInitialJson = value;
  }

  get primaryActionLabel(): string {
    return this.selectedMenuId === 'ask' ? 'Generate' : 'Add filter';
  }

  get canPerformPrimaryAction(): boolean {
    if (this.selectedMenuId === 'ask') {
      return !!this.askPromptValue.trim();
    }
    return this.filterRows.length > 0 && this.filterRows.every(isFilterRowValid) && !hasDuplicateFilterNames(this.filterRows);
  }

  onPrimaryAction(): void {
    if (this.selectedMenuId === 'ask') {
      // TODO: wire to the real ASK-generation endpoint once available; for now just move to Visual Editor for review.
      this.selectedMenuId = 'visual-editor';
      return;
    }
    this.filterCreated.emit({
      filters: this.filterRows,
      jsonEditor: this.jsonEditorInitialJson,
      sqlQuery: this.sqlQueryCode
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }
}
